/**
 * ConfirmChangeModal — Paket C (premium-checkout-redesign)
 *
 * Bestätigungs-Dialog für einen Planwechsel ohne Zuzahlung (Upgrade mit
 * due=0 sowie jeder Downgrade). Führt changePlan(targetPlanId, durationMonths)
 * aus; bei Erfolg meldet er sich per onSuccess an den Aufrufer zurück (Toast +
 * Refetch + ggf. Navigation zur Success-Seite obliegen dem Aufrufer).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DemoGuardedButton } from '../../components/DemoGuardedButton'

type ConfirmChangeModalProps = {
  direction: 'up' | 'down'
  targetPlanId: string
  durationMonths: number
  bodyText: string
  changePlan: (planId: string, durationMonths: number) => Promise<void>
  onClose: () => void
  onSuccess: () => void
  onError: (message: string) => void
}

export function ConfirmChangeModal({
  direction,
  targetPlanId,
  durationMonths,
  bodyText,
  changePlan,
  onClose,
  onSuccess,
  onError,
}: ConfirmChangeModalProps) {
  const { t } = useTranslation('subscription')
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleConfirm() {
    setIsSubmitting(true)
    try {
      await changePlan(targetPlanId, durationMonths)
      onSuccess()
    } catch {
      onError(t(`confirmChangeModal.error.${direction}`))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-change-title"
      data-testid="confirm-change-modal"
      className="subscription-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="subscription-modal">
        <h3 id="confirm-change-title" style={{ margin: 0 }}>
          {t('confirmChangeModal.title')}
        </h3>
        <p style={{ margin: '14px 0', fontSize: 14 }} data-testid="confirm-change-body">
          {bodyText}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            className="btn btn-ghost"
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="confirm-change-cancel"
          >
            {t('common:actions.cancel')}
          </button>
          <DemoGuardedButton
            className="btn btn-primary"
            onClick={() => { void handleConfirm() }}
            disabled={isSubmitting}
            data-testid="confirm-change-go"
          >
            {isSubmitting
              ? t('confirmChangeModal.confirmLoading')
              : t(`confirmChangeModal.confirm.${direction}`)}
          </DemoGuardedButton>
        </div>
      </div>
    </div>
  )
}
